import { CacheHelper } from "../../../../utilities/CacheHelper";
import { logger } from "../../../../utilities/logger";
import { ContentManagerCopyMoveModeEnum } from "../../components/enums/ContentManagerCopyMoveModeEnum";
import { ContentManagerDirectoryEnum } from "../../components/enums/ContentManagerDirectoryEnum";
import { ContentManagerHomeEnum } from "../../components/enums/ContentManagerHomeEnum";
import { FilterTypeEnum } from "../../components/enums/FilterTypeEnum";
import { DocubirdCache } from "../../config/constants";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { typedAction } from "../helpers";
import { FilterItem, FilterSection, IContentManagerDirectoryState, IContentManagerExchange, IContentManagerExchangeProgress, IContentManagerMoveTo } from "../models/reduxModels";
import {
    SET_CONTENT_MANAGER_DIRECTORY,
    RESET_CONTENT_MANAGER_DIRECTORY,
    SET_IS_CONTENT_MANAGER_CONTENT_LOADING,
    SET_CONTENT_MANAGER_FILTER_VALUE,
    SET_CONTENT_MANAGER_SEARCH_VALUE,
    SET_CONTENT_MANAGER_SEARCH_FILTER_TYPE,
    TOGGLE_CONTENT_MANAGER_RELOAD,
    SET_CONTENT_MANAGER_FILTER_FIELDS,
    SET_CONTENT_MANAGER_FILTER_SECTIONS,
    SET_CONTENT_MANAGER_FILTER_SECTIONS_RESET,
    SET_CONTENT_MANAGER_FILTER_SECTION_ITEM,
    SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS,
    SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS_FROM_RANGE,
    SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SHOW_MORE,
    SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SLIDER,
    SET_CONTENT_MANAGER_MOVETO_MOVETOENABLED,
    SET_CONTENT_MANAGER_MOVETO_WORKING_DIRECTORIES,
    SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES,
    SET_CONTENT_MANAGER_MOVETO_SITEURL,
    SET_CONTENT_MANAGER_MOVETO_SOURCE_SERVERRELPATH,
    SET_CONTENT_MANAGER_MOVETO_BUSYMOVINGFILES,
    SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES_ISBUSY_PROGRESS,
    SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS,
    SET_CONTENT_MANAGER_MOVETO_ADDTO_COPYJOBRESULTS,
    SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS_IDS_TO_REMOVE,
    SET_CONTENT_MANAGER_MOVETO_COMPLETED_ITEM_COUNT,
    SET_CONTENT_MANAGER_MOVETO,
    SET_CONTENT_MANAGER_MOVETO_ERROR,
    SET_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED,
    TOGGLE_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED,
    SET_CONTENT_MANAGER_EXCHANGE,
    SET_CONTENT_MANAGER_EXCHANGE_PROGRESS,
    SET_CONTENT_MANAGER_EXCHANGE_ADDITIONAL_MAILBOXES,
    SET_CONTENT_MANAGER_SELECTED_HOME_SECTION,
    RESET_CONTENT_MANAGER_MOVETO
} from "./actionTypes";

const initialState: IContentManagerDirectoryState = {
    selectedHomeSection: ContentManagerHomeEnum.Pinned,
    isContentLoading: false,
    isSideMenuCollapsed: false,
    filterValue: "",
    searchValue: {
        value: "",
        filterType: ""
    },
    toggleReload: false,
    directory: ContentManagerDirectoryEnum.Home,
    filterFields: [],
    filterSections: [],
    moveTo: {
        mode: null,
        moveToEnabled: false,
        workingDirectories: [],
        selectedFiles: [],
        siteUrl: "",
        sourceSiteUrl: "",
        sourceServerRelPath: "",
        destinationServerRelPath: "",
        busyFetchingFiles: false,
        busyMovingFiles: false,
        copyJobResults: [],
        isMoveComplete: false,
        isMoveCancelled: false,
        isMoveInProgress: false,
        isPreparingJobs: false,
        destinationDirectory: null,
        sourceDirectory: null,
        progressCountTotal: 0,
        progressCountCompleted: 0
    },
    exchange: {
        selectedFolderId: "",
        selectedFolderName: "",
        selectedAccount: "",
        additionalMailboxes: undefined
    }
};

export const setContentManagerDirectory = (directory: ContentManagerDirectoryEnum, registerHistory: boolean = true) => {
    return typedAction(SET_CONTENT_MANAGER_DIRECTORY, { directory, registerHistory });
}

export const resetContentManagerDirectory = (registerHistory: boolean = true) => {
    return typedAction(RESET_CONTENT_MANAGER_DIRECTORY, registerHistory);
}

export const setIsContentManagerContentLoading = (isContentLoading: boolean) => {
    return typedAction(SET_IS_CONTENT_MANAGER_CONTENT_LOADING, isContentLoading);
}

export const setIsContentManagerSideMenuCollapsed = (isSideMenuCollapsed: boolean) => {
    return typedAction(SET_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED, isSideMenuCollapsed);
}

export const toggleIsContentManagerSideMenuCollapsed = () => {
    return typedAction(TOGGLE_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED);
}

export const setContentManagerFilterValue = (filterValue: string) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_VALUE, filterValue);
}

export const setContentManagerSearchValue = (searchValue: string) => {
    return typedAction(SET_CONTENT_MANAGER_SEARCH_VALUE, searchValue);
}

export const setContentManagerSearchFilterType = (filterValue: FilterTypeEnum | "") => {
    return typedAction(SET_CONTENT_MANAGER_SEARCH_FILTER_TYPE, filterValue);
}

export const toggleContentManagerReload = () => {
    return typedAction(TOGGLE_CONTENT_MANAGER_RELOAD);
}

export const setContentManagerFilterFields = (filterFields: any[]) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_FIELDS, filterFields);
}

export const setContentManagerFilterSections = (filterSections: FilterSection[]) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTIONS, filterSections);
}

export const setContentManagerFilterSectionsReset = () => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTIONS_RESET);
}

export const setContentManagerFilterSectionItem = (filterSection: FilterSection, sectionItem: FilterItem, isChecked: boolean) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTION_ITEM, { filterSection, sectionItem, isChecked });
}

export const setContentManagerFilterSectionItems = (filterSection: FilterSection, sectionItems: FilterItem[]) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS, { filterSection, sectionItems });
}

export const setContentManagerFilterSectionItemsFromRange = (filterSection: FilterSection, daysOffset: number) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS_FROM_RANGE, { filterSection, daysOffset });
}

export const setContentManagerFilterSectionToggleShowMOre = (filterSection: FilterSection) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SHOW_MORE, { filterSection });
}

export const setContentManagerFilterSectionToggleSlider = (filterSection: FilterSection) => {
    return typedAction(SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SLIDER, { filterSection });
}

export const setContentManagerMoveToMoveToEnabled = (moveToEnabled: boolean) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_MOVETOENABLED, { moveToEnabled });
}

export const setContentManagerMoveToWorkingDirectory = (locations: IPageSettings[]) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_WORKING_DIRECTORIES, { locations });
}

export const setContentManagerMoveToSelectedFiles = (selectedFiles: any[]) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES, { selectedFiles });
}

export const setContentManagerMoveToSiteUrl = (siteUrl: string) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_SITEURL, { siteUrl });
}

export const setContentManagerMoveToSourceServerRelPath = (sourceServerRelPath: string) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_SOURCE_SERVERRELPATH, { sourceServerRelPath });
}

export const setContentManagerMoveToBusyMovingFiles = (busyMovingFiles: boolean) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_BUSYMOVINGFILES, { busyMovingFiles });
}

export const setContentManagerMoveToSelectedFilesIsBusyProgress = (selectedIds: string[], isBusy: boolean) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES_ISBUSY_PROGRESS, { selectedIds, isBusy });
}

export const setContentManagerMoveToCopyJobResults = (copyJobResults: any[]) => {
    CacheHelper.set(DocubirdCache.copyJobResults, copyJobResults);
    return typedAction(SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS, { copyJobResults });
}

export const setContentManagerMoveToCopyJobResultsIdsToRemove = (idsToRemove: string[]) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS_IDS_TO_REMOVE, { idsToRemove });
}

export const setContentManagerMoveToAddToCopyJobResults = (copyJobResults: any[]) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_ADDTO_COPYJOBRESULTS, { copyJobResults });
}

export const setContentManagerMoveTo = (moveTo: IContentManagerMoveTo) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO, { moveTo });
}

export const setContentManagerMoveToError = (error?: string) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_ERROR, { error });
}

export const setContentManagerExchange = (exchange: IContentManagerExchange) => {
    return typedAction(SET_CONTENT_MANAGER_EXCHANGE, { exchange });
}

export const setContentManagerExchangeProgress = (progress: IContentManagerExchangeProgress) => {
    return typedAction(SET_CONTENT_MANAGER_EXCHANGE_PROGRESS, { progress });
}

export const setContentManagerSelectedHomeSection = (section: ContentManagerHomeEnum) => {
    return typedAction(SET_CONTENT_MANAGER_SELECTED_HOME_SECTION, section);
}

export const setContentManagerExchangeAdditionalMailboxes = (additionalMailboxes: string[]) => {
    return typedAction(SET_CONTENT_MANAGER_EXCHANGE_ADDITIONAL_MAILBOXES, { additionalMailboxes });
}

export const resetContentManagerMoveTo = () => {
    return typedAction(RESET_CONTENT_MANAGER_MOVETO);
}

export const incrementContentManagerMoveToCompletedItemCount = (completedItemCount: number) => {
    return typedAction(SET_CONTENT_MANAGER_MOVETO_COMPLETED_ITEM_COUNT, { completedItemCount });
}

type ContentManagerDirectoryAction = ReturnType<
    typeof setContentManagerDirectory |
    typeof resetContentManagerDirectory |
    typeof setIsContentManagerContentLoading |
    typeof setIsContentManagerSideMenuCollapsed |
    typeof toggleIsContentManagerSideMenuCollapsed |
    typeof setContentManagerFilterValue |
    typeof setContentManagerSearchValue |
    typeof setContentManagerSearchFilterType |
    typeof toggleContentManagerReload |
    typeof setContentManagerFilterFields |
    typeof setContentManagerFilterSections |
    typeof setContentManagerFilterSectionsReset |
    typeof setContentManagerFilterSectionItem |
    typeof setContentManagerFilterSectionItems |
    typeof setContentManagerFilterSectionItemsFromRange |
    typeof setContentManagerFilterSectionToggleShowMOre |
    typeof setContentManagerFilterSectionToggleSlider |
    typeof setContentManagerMoveToMoveToEnabled |
    typeof setContentManagerMoveToWorkingDirectory |
    typeof setContentManagerMoveToSelectedFiles |
    typeof setContentManagerMoveToSiteUrl |
    typeof setContentManagerMoveToSourceServerRelPath |
    typeof setContentManagerMoveToBusyMovingFiles |
    typeof setContentManagerMoveToSelectedFilesIsBusyProgress |
    typeof setContentManagerMoveToCopyJobResults |
    typeof setContentManagerMoveToCopyJobResultsIdsToRemove |
    typeof setContentManagerMoveToAddToCopyJobResults |
    typeof setContentManagerMoveTo |
    typeof setContentManagerMoveToError |
    typeof setContentManagerExchange |
    typeof setContentManagerExchangeProgress |
    typeof setContentManagerExchangeAdditionalMailboxes |
    typeof setContentManagerSelectedHomeSection |
    typeof resetContentManagerMoveTo |
    typeof incrementContentManagerMoveToCompletedItemCount |
    typeof setContentManagerFilterValue
>;

export function contentManagerDirectoryReducer(
    state = initialState,
    action: ContentManagerDirectoryAction
): IContentManagerDirectoryState {
    switch (action.type) {
        case SET_CONTENT_MANAGER_DIRECTORY:
            if (action.payload.registerHistory === true) {
                registerHistoryEntry(action.payload.directory);
            }
            return { ...state, directory: action.payload.directory };
        case RESET_CONTENT_MANAGER_DIRECTORY:
            if (action.payload === true) {
                registerHistoryEntry(ContentManagerDirectoryEnum.Home);
            }
            return {
                ...initialState,
                searchValue: state.searchValue,
                toggleReload: state.toggleReload,
                moveTo: state.moveTo,
                exchange: state.exchange
            };
        case SET_IS_CONTENT_MANAGER_CONTENT_LOADING:
            return { ...state, isContentLoading: action.payload };
        case SET_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED:
            return { ...state, isSideMenuCollapsed: action.payload };
        case TOGGLE_IS_CONTENT_MANAGER_SIDEMENU_COLLAPSED:
            return { ...state, isSideMenuCollapsed: !state.isSideMenuCollapsed };
        case SET_CONTENT_MANAGER_FILTER_VALUE:
            return { ...state, filterValue: action.payload };
        case SET_CONTENT_MANAGER_SEARCH_VALUE:
            return { ...state, searchValue: { ...state.searchValue, value: action.payload } };
        case SET_CONTENT_MANAGER_SEARCH_FILTER_TYPE:
            return { ...state, searchValue: { ...state.searchValue, filterType: action.payload } };
        case TOGGLE_CONTENT_MANAGER_RELOAD:
            return { ...state, toggleReload: !state.toggleReload };
        case SET_CONTENT_MANAGER_FILTER_FIELDS:
            return { ...state, filterFields: action.payload };
        case SET_CONTENT_MANAGER_FILTER_SECTIONS:
            return { ...state, filterSections: action.payload };
        case SET_CONTENT_MANAGER_SELECTED_HOME_SECTION:
            return { ...state, selectedHomeSection: action.payload };
        case SET_CONTENT_MANAGER_FILTER_SECTIONS_RESET: {
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    return {
                        ...section,
                        showMoreExpanded: false,
                        daysOffset: null,
                        items: section.items.map(item => {
                            return {
                                ...item,
                                selected: false
                            }
                        })
                    }
                })
            }
            return newState;
        }
        case SET_CONTENT_MANAGER_FILTER_SECTION_ITEM: {
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    if (section.filterField.RealFieldName === action.payload.filterSection.filterField.RealFieldName) {
                        return {
                            ...section,
                            daysOffset: null,
                            items: section.items.map(item => {
                                if (item.title === action.payload.sectionItem.title) {
                                    return {
                                        ...item,
                                        selected: action.payload.isChecked
                                    }
                                }
                                else {
                                    return item
                                }
                            })
                        }
                    }
                    else {
                        return section
                    }
                })
            }
            return newState;
        }
        case SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS: {
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    if (section.filterField.RealFieldName === action.payload.filterSection.filterField.RealFieldName) {
                        return {
                            ...section,
                            items: [...action.payload.sectionItems]
                        }
                    }
                    else {
                        return section
                    }
                })
            }
            return newState;
        }
        case SET_CONTENT_MANAGER_FILTER_SECTION_ITEMS_FROM_RANGE: {
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    if (section.filterField.RealFieldName === action.payload.filterSection.filterField.RealFieldName) {
                        return {
                            ...section,
                            daysOffset: action.payload.daysOffset
                        }
                    }
                    else {
                        return section
                    }
                })
            }
            return newState;
        }
        case SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SHOW_MORE:
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    if (section.filterField.RealFieldName === action.payload.filterSection.filterField.RealFieldName) {
                        return {
                            ...section,
                            showMoreExpanded: !action.payload.filterSection.showMoreExpanded
                        }
                    }
                    else {
                        return section
                    }

                })
            }
            return newState;
        case SET_CONTENT_MANAGER_FILTER_SECTION_TOGGLE_SLIDER: {
            const newState = {
                ...state,
                filterSections: state.filterSections.map(section => {
                    if (section.filterField.RealFieldName === action.payload.filterSection.filterField.RealFieldName) {
                        return {
                            ...section,
                            showSlider: !action.payload.filterSection.showSlider,
                            items: section.items.map(item => {
                                return {
                                    ...item,
                                    selected: false
                                }
                            })
                        }
                    }
                    else {
                        return section
                    }

                })
            }
            return newState;
        }
        case SET_CONTENT_MANAGER_MOVETO_MOVETOENABLED:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    moveToEnabled: action.payload.moveToEnabled
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_WORKING_DIRECTORIES:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    workingDirectories: [...action.payload.locations]
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    selectedFiles: [...action.payload.selectedFiles]
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_SITEURL:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    siteUrl: action.payload.siteUrl
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_SOURCE_SERVERRELPATH:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    sourceServerRelPath: action.payload.sourceServerRelPath
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_BUSYMOVINGFILES:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    busyMovingFiles: action.payload.busyMovingFiles
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_SELECTED_FILES_ISBUSY_PROGRESS:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    copyJobResultsCompletedIds: [...action.payload.selectedIds],
                    selectedFiles: state.moveTo.selectedFiles.map(selectedFile => {
                        if (action.payload.selectedIds.length === 0) {
                            return {
                                ...selectedFile,
                                isBusy: action.payload.isBusy
                            }
                        }
                        else {
                            const uniqueId = selectedFile.UniqueId.toLowerCase().replace('{', '').replace('}', '')
                            if (action.payload.selectedIds.includes(uniqueId)) {
                                return {
                                    ...selectedFile,
                                    isBusy: action.payload.isBusy
                                }
                            } else {
                                return { ...selectedFile }
                            }
                        }
                    })
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    copyJobResults: [...action.payload.copyJobResults]
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_COPYJOBRESULTS_IDS_TO_REMOVE:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    copyJobResultsCompletedIds: [...action.payload.idsToRemove]
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_ADDTO_COPYJOBRESULTS:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    copyJobResults: [...state.moveTo.copyJobResults, ...action.payload.copyJobResults]
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_COMPLETED_ITEM_COUNT:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    progressCountCompleted: (state.moveTo.progressCountCompleted ?? 0) + action.payload.completedItemCount
                }
            };
        case SET_CONTENT_MANAGER_MOVETO:
            if (action.payload.moveTo.moveToEnabled === false
                && (state.moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails
                    || state.moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails)) {
                CacheHelper.remove(DocubirdCache.selectedExchangeItems);
                CacheHelper.remove(DocubirdCache.spEmailPropertyMappings);
            }

            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    mode: action.payload.moveTo.mode != undefined ? action.payload.moveTo.mode : state.moveTo.mode,
                    moveToEnabled: action.payload.moveTo.moveToEnabled != undefined ? action.payload.moveTo.moveToEnabled : state.moveTo.moveToEnabled,
                    workingDirectories: action.payload.moveTo.workingDirectories ? [...action.payload.moveTo.workingDirectories] : [...state.moveTo.workingDirectories],
                    selectedFiles: action.payload.moveTo.selectedFiles ? [...action.payload.moveTo.selectedFiles] : [...state.moveTo.selectedFiles],
                    siteUrl: action.payload.moveTo.siteUrl ? action.payload.moveTo.siteUrl : state.moveTo.siteUrl,
                    sourceSiteUrl: action.payload.moveTo.sourceSiteUrl ? action.payload.moveTo.sourceSiteUrl : state.moveTo.sourceSiteUrl,
                    sourceServerRelPath: action.payload.moveTo.sourceServerRelPath ? action.payload.moveTo.sourceServerRelPath : state.moveTo.sourceServerRelPath,
                    destinationServerRelPath: action.payload.moveTo.destinationServerRelPath ? action.payload.moveTo.destinationServerRelPath : state.moveTo.destinationServerRelPath,
                    busyFetchingFiles: action.payload.moveTo.busyFetchingFiles != undefined ? action.payload.moveTo.busyFetchingFiles : state.moveTo.busyFetchingFiles,
                    busyMovingFiles: action.payload.moveTo.busyMovingFiles != undefined ? action.payload.moveTo.busyMovingFiles : state.moveTo.busyMovingFiles,
                    isMoveComplete: action.payload.moveTo.isMoveComplete != undefined ? action.payload.moveTo.isMoveComplete : state.moveTo.isMoveComplete,
                    isMoveCancelled: action.payload.moveTo.isMoveCancelled != undefined ? action.payload.moveTo.isMoveCancelled : state.moveTo.isMoveCancelled,
                    isMoveInProgress: action.payload.moveTo.isMoveInProgress != undefined ? action.payload.moveTo.isMoveInProgress : state.moveTo.isMoveInProgress,
                    isPreparingJobs: action.payload.moveTo.isPreparingJobs != undefined ? action.payload.moveTo.isPreparingJobs : state.moveTo.isPreparingJobs,
                    destinationDirectory: action.payload.moveTo.destinationDirectory ? action.payload.moveTo.destinationDirectory : state.moveTo.destinationDirectory,
                    sourceDirectory: action.payload.moveTo.sourceDirectory ? action.payload.moveTo.sourceDirectory : state.moveTo.sourceDirectory,
                    progressCountCompleted: action.payload.moveTo.progressCountCompleted != undefined ? action.payload.moveTo.progressCountCompleted : state.moveTo.progressCountCompleted,
                    progressCountTotal: action.payload.moveTo.progressCountTotal != undefined ? action.payload.moveTo.progressCountTotal : state.moveTo.progressCountTotal,
                    error: action.payload.moveTo.error != undefined ? action.payload.moveTo.error : state.moveTo.error,
                    copyJobResults: action.payload.moveTo.copyJobResults != undefined ? [...action.payload.moveTo.copyJobResults] : state.moveTo.copyJobResults,
                    copyJobResultsCompletedIds: action.payload.moveTo.copyJobResultsCompletedIds != undefined ? [...action.payload.moveTo.copyJobResultsCompletedIds] : state.moveTo.copyJobResultsCompletedIds
                }
            };
        case SET_CONTENT_MANAGER_MOVETO_ERROR:
            return {
                ...state,
                moveTo: {
                    ...state.moveTo,
                    error: action.payload.error,
                }
            };
        case SET_CONTENT_MANAGER_EXCHANGE:
            return {
                ...state,
                exchange: {
                    ...state.exchange,
                    selectedFolderId: action.payload.exchange.selectedFolderId,
                    selectedFolderName: action.payload.exchange.selectedFolderName,
                    selectedAccount: action.payload.exchange.selectedAccount
                }
            };
        case SET_CONTENT_MANAGER_EXCHANGE_PROGRESS:
            return {
                ...state,
                exchange: {
                    ...state.exchange,
                    progress: {
                        taskId: action.payload.progress?.taskId ? action.payload.progress.taskId : "",
                        status: action.payload.progress?.status ? action.payload.progress.status : undefined,
                        message: action.payload.progress?.message ? action.payload.progress.message : "",
                        logs: action.payload.progress?.logs ? action.payload.progress.logs : [],
                    }
                }
            };
        case SET_CONTENT_MANAGER_EXCHANGE_ADDITIONAL_MAILBOXES:
            return {
                ...state,
                exchange: {
                    ...state.exchange,
                    additionalMailboxes: action.payload.additionalMailboxes?.length > 0 ? [...action.payload.additionalMailboxes] : []
                }
            };
        case RESET_CONTENT_MANAGER_MOVETO:
            return {
                ...state,
                moveTo: { ...initialState.moveTo }
            };
        default:
            return state;
    }
}

export const registerHistoryEntry = (contentManagerDirectory: ContentManagerDirectoryEnum, replace: boolean = false) => {
    if (history?.pushState) {
        try {
            const cleanUrl = window.location.href
                .split("?")[0]
                .split("#")[0];

            let locationName: string;
            switch (contentManagerDirectory) {
                case ContentManagerDirectoryEnum.Home:
                    locationName = "Home";
                    break;
                case ContentManagerDirectoryEnum.SharePoint:
                    locationName = "SharePoint";
                    break;
                case ContentManagerDirectoryEnum.Teams:
                    locationName = "Teams";
                    break;
                case ContentManagerDirectoryEnum.Docubird:
                    locationName = "Docubird";
                    break;
                case ContentManagerDirectoryEnum.Exchange:
                    locationName = "Exchange";
                    break;
                default:
                    locationName = "Home";
                    break;
            }
            if (!replace) {
                history.pushState(contentManagerDirectory, "", cleanUrl + "#" + locationName);
            }
            else {
                history.replaceState(contentManagerDirectory, "", cleanUrl + "#" + locationName);
            }
            document.title = locationName + " | Docubird";
        } catch (error) {
            logger(error, "contentManagerDirectory.ts (registerHistoryEntry)");
        }
    }
}